import React from "react";
import { useTranslation } from "react-i18next";
import CardHeader from "../ui/CardHeader";
import EmptyChart from "../ui/EmptyChart";
import { formatInt } from "../../utils/formatters";

/**
 * CallsByBusinessCard — top-N organisations by call count, drawn as
 * horizontal bars relative to the largest entry in the list.
 *
 * Props:
 *   rows: { org_id, org_name, count }[]   — already sorted desc by API
 *   className: extra classes for the outer card
 */
const CallsByBusinessCard = ({ rows, className = "" }) => {
  const { t } = useTranslation();
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className={`flat-card rounded-xl p-6 sm:p-8 ${className}`}>
      <CardHeader
        icon="corporate_fare"
        title={t("analytics.callsByBusiness.title")}
        subtitle={t("analytics.callsByBusiness.subtitle", {
          count: rows.length,
        })}
      />
      {rows.length === 0 ? (
        <EmptyChart label={t("analytics.callsByBusiness.noData")} height={120} />
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => {
            const pct = (r.count / max) * 100;
            return (
              <li key={r.org_id} className="space-y-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-on-surface-light dark:text-on-surface truncate">
                    {r.org_name}
                  </span>
                  <span className="text-xs font-mono text-on-surface-light-variant dark:text-on-surface-variant shrink-0">
                    {formatInt(r.count)}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-surface-light-low dark:bg-surface-container-low overflow-hidden">
                  <div
                    className="h-full bg-brand"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CallsByBusinessCard;
